import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "../api";
import { STATUSES, ROLES, COMPANY_TYPE_LABEL } from "../projectMeta";

const EMPTY = { name: "", status: "CONTACT", start_date: "", description: "" };

export default function ProjectForm() {
  const { id } = useParams();
  const editing = Boolean(id);
  const [form, setForm] = useState(EMPTY);
  // 신규 등록 시 주업체 선택
  const [companies, setCompanies] = useState(editing ? [] : null);
  const [mainCompanyId, setMainCompanyId] = useState("");
  const [mainRole, setMainRole] = useState("CLIENT");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(editing);
  const navigate = useNavigate();

  useEffect(() => {
    if (editing) {
      (async () => {
        try {
          const p = await api.get(`/projects/${id}`);
          setForm({
            name: p.name || "",
            status: p.status || "CONTACT",
            start_date: p.start_date || "",
            description: p.description || "",
          });
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      })();
    } else {
      // 주업체 선택용 업체 목록
      (async () => {
        try {
          setCompanies(await api.get("/companies"));
        } catch (err) {
          setError(err.message);
        }
      })();
    }
  }, [editing, id]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(e) {
    e.preventDefault();
    if (busy) return;
    if (!form.name.trim()) {
      setError("프로젝트명은 필수입니다.");
      return;
    }
    setBusy(true);
    setError("");
    const payload = { ...form, start_date: form.start_date || null };
    try {
      if (editing) {
        await api.post(`/projects/${id}`, payload);
        navigate(`/sstoa/projects/${id}`, { replace: true });
      } else {
        if (mainCompanyId) {
          payload.main_company_id = Number(mainCompanyId);
          payload.main_company_role = mainRole;
        }
        const created = await api.post("/projects", payload);
        navigate(`/sstoa/projects/${created.id}`, { replace: true });
      }
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  if (loading) return <div className="sstoa-empty">불러오는 중…</div>;

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Link to={editing ? `/sstoa/projects/${id}` : "/sstoa/projects"} className="sstoa-back">← 돌아가기</Link>
      </div>
      <h1 className="sstoa-title" style={{ marginBottom: 20 }}>
        {editing ? "프로젝트 수정" : "프로젝트 등록"}
      </h1>

      {error && <div className="sstoa-error" style={{ marginBottom: 16, maxWidth: 560 }}>{error}</div>}

      <form className="sstoa-form" onSubmit={onSubmit}>
        <div className="sstoa-row">
          <div className="sstoa-field" style={{ flex: 2 }}>
            <label>프로젝트명 <span className="req">*</span></label>
            <input className="sstoa-input" value={form.name} onChange={upd("name")} disabled={busy} />
          </div>
          <div className="sstoa-field">
            <label>상태</label>
            <select className="sstoa-select" value={form.status} onChange={upd("status")} disabled={busy}>
              {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>

        <div className="sstoa-field" style={{ maxWidth: 240 }}>
          <label>시작일</label>
          <input className="sstoa-input" type="date" value={form.start_date} onChange={upd("start_date")} disabled={busy} />
        </div>

        {!editing && (
          <div className="sstoa-panel sstoa-panel--pad">
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--s-fg-dim)", marginBottom: 12 }}>
              주업체 연결 (선택)
            </div>
            <div className="sstoa-row">
              <div className="sstoa-field" style={{ flex: 2 }}>
                <label>주업체</label>
                <select className="sstoa-select" value={mainCompanyId} onChange={(e) => setMainCompanyId(e.target.value)} disabled={busy || companies === null}>
                  <option value="">{companies === null ? "불러오는 중…" : "선택 안 함"}</option>
                  {(companies || []).map((c) => (
                    <option key={c.id} value={c.id}>{c.name} ({COMPANY_TYPE_LABEL[c.type] || c.type})</option>
                  ))}
                </select>
              </div>
              <div className="sstoa-field">
                <label>역할</label>
                <select className="sstoa-select" value={mainRole} onChange={(e) => setMainRole(e.target.value)} disabled={busy || !mainCompanyId}>
                  {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </div>
            <p style={{ margin: "10px 0 0", fontSize: 12, color: "var(--s-fg-mute)" }}>
              등록된 업체 중에서 주업체를 지정합니다. 협력업체는 생성 후 상세 화면에서 추가할 수 있습니다.
            </p>
          </div>
        )}

        <div className="sstoa-field">
          <label>설명</label>
          <textarea className="sstoa-textarea" value={form.description} onChange={upd("description")} disabled={busy} />
        </div>

        <div className="sstoa-actions">
          <button type="submit" className="sstoa-btn sstoa-btn--primary" disabled={busy}>
            {busy ? "저장 중…" : "저장"}
          </button>
          <Link to={editing ? `/sstoa/projects/${id}` : "/sstoa/projects"} className="sstoa-btn">취소</Link>
        </div>
      </form>
    </div>
  );
}
